import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { CommunicationService } from '../services/communication.service';
import { Router } from '@angular/router';
import { GlobalSearchService } from '../services/global-search.service';
import { AlertsService } from '../services/alerts.service';
import { CookieService } from 'ngx-cookie';
import { DataSharingService } from '../services/data-sharing.service';
import { ZintGrowService } from '../services/zint-grow.service';
import { NotificationService } from '../services/notification.service';
import { publicMethods } from '../globals';
import { Subscription } from 'rxjs';
import {filter, skip, take} from 'rxjs/operators';
import {forkJoin} from 'rxjs/internal/observable/forkJoin';
declare var $: any;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class AppComponent implements OnInit {
  accountEmail: any;
  accountId: number;
  isSuperUser: any;
  isParentOrgSuperUser: any;
  orgId: any;
  parentOrgId: any;
  urlNoParams: string;
  companyNameSearch: '';
  shortlistedCompaniesByName: Array<any> = [];
  notifications: Array<any> = [];
  title = 'Zint';
  isOnDashboard = false;

  hasPropensityScoreAccess: any;
  hasSalesforceAccessMessage: any;
  hasSalesforceV2AccessMessage: any;
  hasHubspotAccess: boolean;
  hasDeepCrawlAccess: boolean;
  hasRecentAccountsAccess: boolean;
  hasMultiplePostcodeSearchAccess: boolean;
  hasCompanyMatcherAccess: boolean;
  hasCrossSellAccess: boolean;
  hasMarketMappingReportAccess: boolean;
  hasTerritoryAssignmentAccess: boolean;
  hasZintGrowAccess: boolean;
  hasCalendarIntegrationAccess: boolean;

  showCookieBanner = false;

  zgResultCountSubscription: Subscription;
  zgUnreadCount: number = 0;
  magicInsightsUnreadCount: number = 0;

  constructor(
    private communicationService: CommunicationService,
    private alertsService: AlertsService,
    public appComponentRouter: Router,
    private globalSearchService: GlobalSearchService,
    private cookieservice: CookieService,
    private pubMethod: publicMethods,
    private dataShareStore: DataSharingService,
    private zintGrowService: ZintGrowService,
    private notificationService: NotificationService
  ) {}

  isLoadingAlerts = this.alertsService.isLoading;
  isLoadingNameSearch = this.globalSearchService.isLoading;

  get aggregatedNotificationCount(): number {
    const alertsCount = this.notifications ? this.notifications.length : 0;
    return (this.zgUnreadCount || 0) + (this.magicInsightsUnreadCount || 0) + alertsCount;
  }

  ngOnInit(): void {
    if (window.location.pathname === '/logout') {
      setTimeout(() => {
        this.updateAllUserDetails();
      }, 5000);
    } else {
      this.checkIsOnDashboard();
      this.updateAllUserDetails();
    }
    const zintCookiesAccepted = this.cookieservice.get('zintCookiesAccepted');
    if (!zintCookiesAccepted) {
      this.showCookieBanner = true;
    }
  }

  checkIsOnDashboard(): void {
    const rootPath = window.location.pathname === '/';
    const dashboardPath = window.location.pathname === '/dashboard';
    const loginPath = window.location.pathname === '/login';

    this.isOnDashboard = rootPath || dashboardPath || loginPath;
  }

  async updateAllUserDetails(): Promise<void> {
    await this.getAllUserAccountData();

    if (!this.accountId) {
        return;
    }

    this.getZGUnreadNotificationsCount();
    this.checkMagicInsightsCount();

    this.alertsService
        .getAlertNotifications()
        .subscribe(data => this.updateNotificationAlerts(data));

    this.setUserProduktlyDetails();
  }

  // New method to handle the UI jump between menus
  openAlertsFromBurgerMenu(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    // 1. Open the main burger menu
    $('#burgerMenuToggle').dropdown('toggle');

    // 2. Wait a tick for the DOM to update, then open the specific Alerts submenu
    setTimeout(() => {
      $('#burgerAlertsLink').dropdown('toggle');
    }, 50);
  }

  checkMagicInsightsCount(): void {
    const lastCheckKey = 'lastMagicInsightsAlertCountCheck';
    const countKey = 'MagicInsightsAlertCount';
    const NOTIFICATION_TYPE = 'magic_insights_progress';

    const lastCheckTimestamp = localStorage.getItem(lastCheckKey);
    const now = new Date().getTime();

    if (lastCheckTimestamp && (now - parseInt(lastCheckTimestamp, 10)) < 60000) {
      const storedCount = localStorage.getItem(countKey);
      this.magicInsightsUnreadCount = storedCount ? parseInt(storedCount, 10) : 0;
    } else {
      this.notificationService.getNotificationCount(NOTIFICATION_TYPE).subscribe((count: number) => {
          this.magicInsightsUnreadCount = count;
          localStorage.setItem(countKey, count.toString());
          localStorage.setItem(lastCheckKey, now.toString());
      }, (error) => {
          console.error('Failed to fetch Magic Insights count', error);
      });
    }
  }

  setUserProduktlyDetails(): void{
    // @ts-ignore
    window.Produktly.identifyUser(
      this.accountId,
      {
        email: this.accountEmail,
        orgId: this.orgId,
        parentOrgId: this.parentOrgId,
        isSuperuser: this.isSuperUser,
        isParentOrgSuperUser: this.isParentOrgSuperUser,
      }
    )
  }

  async getAllUserAccountData(): Promise<void> {
    interface UserDetails {
      accountId: number;
      accountEmail: any;
      isSuperUser: any;
      isParentOrgSuperUser: any;
      orgId: any;
      parentOrgId: any;
      hasPropensityScoreAccess: any;
      hasSalesforceAccessMessage: any;
      hasSalesforceV2AccessMessage: any;
      hasHubspotAccess: boolean;
      hasDeepCrawlAccess: boolean;
      hasRecentAccountsAccess: boolean;
      hasMultiplePostcodeSearchAccess: boolean;
      hasCompanyMatcherAccess: boolean;
      hasCrossSellAccess: boolean;
      hasMarketMappingReportAccess: boolean;
      hasTerritoryAssignmentAccess: boolean;
      hasCalendarIntegrationAccess: boolean;
      hasZintGrowAccess: boolean;
    }

    const userDetailsObservables = {
      accountId: this.communicationService.getUserAccountId().pipe(filter(id => id !== 0), take(1)),
      accountEmail: this.communicationService.getEmailMessage().pipe(skip(1), take(1)),
      isSuperUser: this.communicationService.getIsSuperUserMessage().pipe(skip(1), take(1)),
      isParentOrgSuperUser: this.communicationService.getIsParentOrgSuperUserMessage().pipe(skip(1), take(1)),
      orgId: this.communicationService.getOrgIdMessage().pipe(skip(1), take(1)),
      parentOrgId: this.communicationService.getParentOrgIdMessage().pipe(skip(1), take(1)),
      hasPropensityScoreAccess: this.communicationService.getHasPropensityScoreAccessMessage().pipe(skip(1), take(1)),
      hasSalesforceAccessMessage: this.communicationService.getHasSalesforceAccessMessage().pipe(skip(1), take(1)),
      hasSalesforceV2AccessMessage: this.communicationService.getHasSalesforceV2AccessMessage().pipe(skip(1), take(1)),
      hasHubspotAccess: this.communicationService.getHubspotAccess().pipe(skip(1), take(1)),
      hasDeepCrawlAccess: this.communicationService.getHasDeepCrawlAccessMessage().pipe(skip(1), take(1)),
      hasRecentAccountsAccess: this.communicationService.getHasRecentAccountsAccessMessage().pipe(skip(1), take(1)),
      hasMultiplePostcodeSearchAccess: this.communicationService.getHasMultiplePostcodeSearchAccessMessage().pipe(skip(1), take(1)),
      hasCompanyMatcherAccess: this.communicationService.getHasCompanyMatcherAccessMessage().pipe(skip(1), take(1)),
      hasCrossSellAccess: this.communicationService.getHasCrossSellAccessMessage().pipe(skip(1), take(1)),
      hasMarketMappingReportAccess: this.communicationService.getHasMarketMappingReportAccessMessage().pipe(skip(1), take(1)),
      hasTerritoryAssignmentAccess: this.communicationService.getHasTerritoryAssignmentAccessMessage().pipe(skip(1), take(1)),
      hasCalendarIntegrationAccess: this.communicationService.getHasCalendarIntegrationAccess().pipe(skip(1), take(1)),
      hasZintGrowAccess: this.communicationService.getHasZintGrowAccessMessage().pipe(skip(1), take(1)),
    };

    const details: UserDetails = await forkJoin(userDetailsObservables).toPromise();

    this.accountId = details.accountId;
    this.accountEmail = details.accountEmail;
    this.isSuperUser = details.isSuperUser;
    this.isParentOrgSuperUser = details.isParentOrgSuperUser;
    this.orgId = details.orgId;
    this.parentOrgId = details.parentOrgId;
    this.hasPropensityScoreAccess = details.hasPropensityScoreAccess;
    this.hasSalesforceAccessMessage = details.hasSalesforceAccessMessage;
    this.hasSalesforceV2AccessMessage = details.hasSalesforceV2AccessMessage;
    this.hasHubspotAccess = details.hasHubspotAccess;
    this.hasDeepCrawlAccess = details.hasDeepCrawlAccess;
    this.hasRecentAccountsAccess = details.hasRecentAccountsAccess;
    this.hasMultiplePostcodeSearchAccess = details.hasMultiplePostcodeSearchAccess;
    this.hasCompanyMatcherAccess = details.hasCompanyMatcherAccess;
    this.hasCrossSellAccess = details.hasCrossSellAccess;
    this.hasMarketMappingReportAccess = details.hasMarketMappingReportAccess;
    this.hasTerritoryAssignmentAccess = details.hasTerritoryAssignmentAccess;
    this.hasCalendarIntegrationAccess = details.hasCalendarIntegrationAccess;
    this.hasZintGrowAccess = details.hasZintGrowAccess;
}

  getZGUnreadNotificationsCount(): void {
    this.subscribeToCountChanges();
    const existingCookie = this.cookieservice.get('zgUnreadCountForUser');
    if (existingCookie) {
      const [cachedCount, userId] = JSON.parse(existingCookie).split('-');
      if (parseInt(userId) === this.accountId) {
        this.zgUnreadCount = parseInt(cachedCount);
        return;
      }
    }

    if (this.hasZintGrowAccess) {
      this.zintGrowService
        .getUnreadGrowNotificationsCount()
        .subscribe(({ count }) => {
          this.setCookieForUnreadCount(count);
          this.zgUnreadCount = count;
        });
    }
  }

  setCookieForUnreadCount(count: number): void {
    const twelveHourExpiry = new Date();
    twelveHourExpiry.setHours(new Date().getHours() + 12);
    const countDetails = `${count}-${this.accountId}`;

    this.pubMethod.setCookieAndExpiry({
      name: 'zgUnreadCountForUser',
      value: countDetails,
      expiry: twelveHourExpiry,
    });
  }

  subscribeToCountChanges(): void {
    if (this.zgResultCountSubscription) return;

    this.zgResultCountSubscription =
      this.dataShareStore.currentZgResultCount.subscribe(newCount => {
        if (newCount !== this.zgUnreadCount) {
          this.zgUnreadCount = newCount;
          this.setCookieForUnreadCount(this.zgUnreadCount);
        }
      });
  }

  updateNotificationAlerts(data): void {
    this.notifications = data.notifications;
  }

  markAlertAsRead($event, alertId): void {
    $event.stopPropagation();
    this.alertsService
      .markAlertCompaniesAsRead(alertId)
      .subscribe(data => this.updateNotificationAlerts(data));
  }

  searchForCompanyByName(): void {
    this.globalSearchService
      .searchCompanyByName(this.companyNameSearch)
      .subscribe(data => (this.shortlistedCompaniesByName = data));
  }

  acceptCookies(): void {
    this.showCookieBanner = false;
    const oneYear = new Date(
      new Date().setFullYear(new Date().getFullYear() + 1)
    );

    this.pubMethod.setCookieAndExpiry({
      expiry: oneYear,
      name: 'zintCookiesAccepted',
      value: 'true',
    });
  }

  ngOnDestroy() {
    if (this.zgResultCountSubscription) {
      this.zgResultCountSubscription.unsubscribe();
    }
  }
}
